import { Component, effect, inject, OnDestroy } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormAvatarComponent, FormInputComponent } from '@shared/forms';
import { UnavailableComponent } from '@shared/components';
import { InstructorFacade } from '@app/store/instructor';

@Component({
    selector: 'app-instructor-edit-modal',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.css'],
    imports: [ReactiveFormsModule, TranslateModule, ButtonModule, FormAvatarComponent, FormInputComponent, UnavailableComponent],
})
export class EditComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly instructorFacade = inject(InstructorFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);

    readonly instructor = this.instructorFacade.instructor;
    readonly loading = this.instructorFacade.loading;
    readonly saving = this.instructorFacade.saving;
    readonly error = this.instructorFacade.error;

    readonly form = this.formBuilder.group({
        firstName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        lastName: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        degree: this.formBuilder.control(''),
        university: this.formBuilder.control(''),
        mediaImageId: this.formBuilder.control<string | null>(null),
    });

    constructor() {
        const id = this.route.parent?.snapshot.params['id'];
        if (id) {
            this.instructorFacade.getInstructor(id);
        }

        effect(() => {
            const instructor = this.instructor();
            if (instructor) {
                this.form.patchValue({
                    ...instructor,
                    mediaImageId: instructor.imageMediaId ? String(instructor.imageMediaId) : null,
                });
            }
        });

        effect(() => {
            if (this.instructorFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.instructorFacade.saving() === false && this.instructorFacade.saved() === false) {
                this.form.enable();
            }
        });
    }

    ngOnDestroy(): void {
        this.instructorFacade.clear();
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        const instructor = this.instructor();
        if (this.form.valid && instructor) {
            this.form.disable();
            this.instructorFacade.updateInstructor(instructor.id, {
                ...this.form.getRawValue(),
                originalMediaImageId: instructor.imageMediaId,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }]);
    }
}
